/**
 * Role Service
 *
 * Service layer for role management API calls
 * Handles all HTTP requests to /api/roles endpoints
 *
 * Features:
 * - CRUD operations (Create, Read, Update, Delete)
 * - Role cloning
 * - Archive and restore operations
 * - Permission management
 * - Filtering by type (system/custom) and status
 *
 * @module roleService
 */

import { apiClient } from '@api'

/**
 * Role Service Class
 *
 * Provides methods for interacting with the roles API
 */
class RoleService {
	/**
	 * Get all roles with optional filters
	 * @param {Object} params - Query parameters
	 * @param {number} params.company_id - Filter by company ID
	 * @param {string} params.type - Filter by type ('system' or 'custom')
	 * @param {string} params.status - Filter by status ('active' or 'archived')
	 * @param {string} params.search - Search by role name
	 * @param {number} params.page - Page number for pagination
	 * @param {number} params.limit - Items per page
	 * @returns {Promise<Object>} API response with roles data
	 */
	async getAll(params = {}) {
		const queryParams = new URLSearchParams()

		if (params.company_id !== undefined) queryParams.append('company_id', params.company_id)
		if (params.type) queryParams.append('type', params.type)
		if (params.status) queryParams.append('status', params.status)
		if (params.search) queryParams.append('search', params.search)
		if (params.page) queryParams.append('page', params.page)
		if (params.limit) queryParams.append('limit', params.limit)

		const query = queryParams.toString()
		return await apiClient.get(`/roles${query ? `?${query}` : ''}`)
	}

	/**
	 * Get single role by ID
	 * @param {string|number} id - Role ID
	 * @returns {Promise<Object>} Role data
	 */
	async getById(id) {
		return await apiClient.get(`/roles/${id}`)
	}

	/**
	 * Get role by slug
	 * @param {string} slug - Role slug
	 * @returns {Promise<Object>} Role data
	 */
	async getBySlug(slug) {
		return await apiClient.get(`/roles/slug/${slug}`)
	}

	/**
	 * Create new custom role
	 * @param {Object} roleData - Role data
	 * @param {string} roleData.name - Role name
	 * @param {string} roleData.slug - Role slug (unique)
	 * @param {string} roleData.description - Role description
	 * @param {number} roleData.company_id - Company ID
	 * @param {Array<string>} roleData.permissions - Array of permission keys
	 * @returns {Promise<Object>} Created role data
	 */
	async create(roleData) {
		return await apiClient.post('/roles', roleData)
	}

	/**
	 * Update existing custom role
	 * @param {string|number} id - Role ID
	 * @param {Object} roleData - Updated role data
	 * @returns {Promise<Object>} Updated role data
	 */
	async update(id, roleData) {
		return await apiClient.put(`/roles/${id}`, roleData)
	}

	/**
	 * Delete role (soft delete)
	 * @param {string|number} id - Role ID
	 * @returns {Promise<Object>} Success response
	 */
	async delete(id) {
		return await apiClient.delete(`/roles/${id}`)
	}

	/**
	 * Restore archived role
	 * @param {string|number} id - Role ID
	 * @returns {Promise<Object>} Restored role data
	 */
	async restore(id) {
		return await apiClient.post(`/roles/${id}/restore`)
	}

	/**
	 * Clone role (create copy)
	 * @param {string|number} id - Role ID to clone
	 * @param {Object} data - Clone customization data
	 * @param {string} data.name - New role name
	 * @param {string} data.slug - New role slug
	 * @returns {Promise<Object>} Cloned role data
	 */
	async clone(id, data) {
		return await apiClient.post(`/roles/${id}/clone`, data)
	}

	/**
	 * Get role permissions
	 * @param {string|number} id - Role ID
	 * @returns {Promise<Object>} Permissions data
	 */
	async getPermissions(id) {
		return await apiClient.get(`/roles/${id}/permissions`)
	}

	/**
	 * Update role permissions
	 * @param {string|number} id - Role ID
	 * @param {Array<string>} permissions - Array of permission keys
	 * @returns {Promise<Object>} Updated permissions data
	 */
	async updatePermissions(id, permissions) {
		return await apiClient.put(`/roles/${id}/permissions`, { permissions })
	}

	/**
	 * Get roles that can be assigned to company users
	 * This excludes super-admin role and includes system roles + company custom roles
	 *
	 * SECURITY: Backend automatically determines company_id from authenticated user
	 * Returns system default roles + company custom roles
	 *
	 * @returns {Promise<Object>} Assignable roles data
	 */
	async getCompanyAssignableRoles() {
		return await apiClient.get('/roles/company-assignable')
	}
}

// Export singleton instance
export const roleService = new RoleService()
export default roleService
